import { API_BASE_URL } from '../config'
import { ExamService } from './examService'

// ─── Sidebar Tree Types ─────────────────────────────────────────────────────

export interface TreeFile {
  path: string // full original name incl. any folder prefix, e.g. "Aufgabe1/Main.java". The API file "name"; combined with the project id via fileUid() to form the fileStore key.
  name: string // display basename, e.g. "Main.java" (last path segment)
}

export interface TreeFolder {
  id: string // unique id within the tree
  name: string // display name
  folders: TreeFolder[] // nested subfolders
  files: TreeFile[] // files directly in this folder
}

export interface SidebarTree {
  root: TreeFolder // the user's namespace as the root folder; its .folders are the projects
  examsRoot: TreeFolder | null // root for exams
  contents: Record<string, string> // maps EVERY file uid -> its content (for seeding fileStore)
}

// ─── File identity ──────────────────────────────────────────────────────────
//
// A file's `path` is only unique WITHIN a project, so two projects that both
// contain e.g. "Main.java" would share the same key — which made both files
// select at once and let content/lookups resolve the wrong project. The unique
// identity of a file across the whole tree is therefore the owning project id
// combined with its path. A NUL separator can never occur in a project id or a
// file path, so the split is unambiguous.
const FILE_UID_SEP = '\u0000'

/** Stable, collision-free key for a file: its owning project id + its path. */
export function fileUid(projectId: string, path: string): string {
  return `${projectId}${FILE_UID_SEP}${path}`
}

/** The owning project id encoded in a file uid ('' if the uid is empty/invalid). */
export function uidProjectId(uid: string): string {
  const i = uid.indexOf(FILE_UID_SEP)
  return i === -1 ? '' : uid.slice(0, i)
}

/** The file path encoded in a file uid (returns the input unchanged if no uid). */
export function uidPath(uid: string): string {
  const i = uid.indexOf(FILE_UID_SEP)
  return i === -1 ? uid : uid.slice(i + 1)
}

// ─── Internal API response types ────────────────────────────────────────────

export interface NamespaceData {
  id: string
  name: string
  storage_namespace_id: string | null
  user_id: string | null
}

export interface ProjectFileData {
  path: string
  content: string | null
}

export interface FolderData {
  id: string
  name: string
  files: string[]
}

export interface ProjectData {
  id: string
  name: string
  title?: string
  data?: ProjectFileData[]
  folders?: FolderData[]
  namespace_id?: string
  visibility?: 'private' | 'internal' | 'public'
  is_public?: boolean
  is_exam?: boolean
  description?: string
}

export interface VersionData {
  id: string
  project_id: string
  commit_sha: string | null
  kind: string
  created_at: string
  message: string | null
}

export interface VersionHistoryResponse {
  versions: VersionData[]
}

export class ProjectAlreadyForkedError extends Error {
  existingProjectId: string
  title: string
  constructor(existingProjectId: string, title: string) {
    super('Project already forked into your namespace')
    this.name = 'ProjectAlreadyForkedError'
    this.existingProjectId = existingProjectId
    this.title = title
  }
}

export type VersionFileStatus = 'added' | 'modified' | 'deleted' | 'renamed'

export interface VersionFileDiff {
  path: string
  old_path: string | null
  status: VersionFileStatus
  diff: string
}

export interface VersionDiffResponse {
  files: VersionFileDiff[]
}
export class ProjectService {
  static async createProject(payload: {
    title: string
    namespace_id: string
    visibility?: 'private' | 'internal' | 'public'
    is_exam?: boolean
    parent_id?: string | null
  }): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to create project')
    return res.json()
  }

  static async getAllProjects(): Promise<ProjectData[]> {
    const res = await fetch(`${API_BASE_URL}/api/projects/`, { credentials: 'include' })
    if (!res.ok) throw new Error('Failed to load projects')
    return res.json()
  }

  static async getProject(projectId: string, versionId?: string): Promise<ProjectData> {
    const url = new URL(`${API_BASE_URL}/api/projects/${projectId}`)
    if (versionId) {
      url.searchParams.append('version_id', versionId)
    }
    const res = await fetch(url.toString(), { credentials: 'include' })
    if (!res.ok) throw new Error('Failed to load project')
    return res.json()
  }

  static async updateProject(
    projectId: string,
    payload: Record<string, unknown>
  ): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to update project')
    return res.json()
  }

  static async deleteProject(projectId: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to delete project')
  }

  static async forkProject(projectId: string, name?: string): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/forkProject/${projectId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name ?? null }),
      credentials: 'include',
    })
    if (!res.ok) {
      if (res.status === 409) {
        const body = await res.json().catch(() => null)
        if (body?.detail?.existing_project_id) {
          throw new ProjectAlreadyForkedError(
            body.detail.existing_project_id,
            body.detail.title ?? ''
          )
        }
      }
      throw new Error('Failed to fork project')
    }
    return res.json()
  }

  static async createFile(
    projectId: string,
    filePath: string,
    content: string = ''
  ): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: filePath, content }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to create file')
    return res.json()
  }

  static async deleteFile(projectId: string, filePath: string): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: filePath }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to delete file')
    return res.json()
  }

  static async renameFile(
    projectId: string,
    filePath: string,
    newPath: string
  ): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: filePath, new_name: newPath }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to rename file')
    return res.json()
  }

  static async moveFile(
    projectId: string,
    filePath: string,
    targetProjectId: string,
    targetPath: string
  ): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: filePath,
        target_project_id: targetProjectId,
        target_path: targetPath,
      }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to move file')
    return res.json()
  }

  static async renameFolder(
    projectId: string,
    path: string,
    newPath: string
  ): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/folders/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, new_path: newPath }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to rename folder')
    return res.json()
  }

  static async deleteFolder(projectId: string, path: string): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/folders/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to delete folder')
    return res.json()
  }

  static async saveProject(
    projectId: string,
    message: string = 'Manual save',
    data?: { path: string; content: string }[]
  ): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, data }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to save project')
    return res.json()
  }

  static async autosaveProject(
    projectId: string,
    data?: { path: string; content: string }[],
    options?: { keepalive?: boolean }
  ): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/autosave`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
      credentials: 'include',
      keepalive: options?.keepalive ?? false,
    })
    if (!res.ok) throw new Error('Failed to autosave project')
    return res.json()
  }

  static async getVersionHistory(projectId: string): Promise<VersionHistoryResponse> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/versions`, {
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to fetch version history')
    return res.json()
  }

  static async getVersionDiff(
    projectId: string,
    versionId: string
  ): Promise<VersionDiffResponse> {
    const res = await fetch(
      `${API_BASE_URL}/api/projects/${projectId}/versions/${versionId}/diff`,
      { credentials: 'include' }
    )
    if (!res.ok) throw new Error('Failed to fetch version diff')
    return res.json()
  }

  static async updateFromParent(projectId: string, parentId: string): Promise<ProjectData> {
    const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}/update-from-parent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: parentId }),
      credentials: 'include',
    })
    if (!res.ok) throw new Error('Failed to update from parent')
    return res.json()
  }

  // ─── Sidebar Tree ──────────────────────────────────────────────────────────

  /**
   * Converts a flat file list from the backend into a nested TreeFolder.
   * Files with no `/` go directly into root.files.
   * Files with `/` in their name create intermediate TreeFolder nodes.
   */
  static buildProjectTree(
    projectId: string,
    projectName: string,
    files: { path: string; content?: string | null }[]
  ): TreeFolder {
    const root: TreeFolder = { id: projectId, name: projectName, folders: [], files: [] }

    for (const file of files) {
      const segments = file.path.split('/')
      const basename = segments[segments.length - 1]

      if (segments.length === 1) {
        // No path prefix → file goes directly into root.files
        root.files.push({ path: file.path, name: basename })
      } else {
        // Navigate or create intermediate folders
        let current: TreeFolder = root
        for (let i = 0; i < segments.length - 1; i++) {
          const seg = segments[i]
          const folderId = `${projectId}/${segments.slice(0, i + 1).join('/')}`
          let next = current.folders.find((f) => f.id === folderId)
          if (!next) {
            next = { id: folderId, name: seg, folders: [], files: [] }
            current.folders.push(next)
          }
          current = next
        }
        current.files.push({ path: file.path, name: basename })
      }
    }

    return root
  }

  static async loadSidebarTree(): Promise<SidebarTree> {
    // 1. Fetch the current user's namespace (single object — the user's OWN namespace)
    const nsRes = await fetch(`${API_BASE_URL}/api/users/namespace`, { credentials: 'include' })
    if (!nsRes.ok) throw new Error('Failed to load user namespace')
    const ns: NamespaceData | null = await nsRes.json()
    if (!ns) throw new Error('No namespace found for current user')

    // 2. Fetch the user's projects under that namespace (list only — use id/title, NOT data)
    const projectsRes = await fetch(`${API_BASE_URL}/api/users/namespace/projects`, {
      credentials: 'include',
    })
    if (!projectsRes.ok) throw new Error('Failed to load projects')
    const projectList: ProjectData[] = await projectsRes.json()

    // 3. Fetch real ProjectData from STORAGE for each project (concurrent)
    const realProjects: ProjectData[] = await Promise.all(
      projectList.map((p) => ProjectService.getProject(p.id))
    )

    // 4. Build a TreeFolder for each project from REAL storage-backed data
    const projectFolders = realProjects.map((p) =>
      ProjectService.buildProjectTree(
        p.id,
        p.title ?? p.name ?? p.id,
        Array.isArray(p.data) ? p.data : []
      )
    )

    // 5. Namespace root → its .folders are the projects
    const root: TreeFolder = {
      id: ns.id,
      name: ns.name ?? 'Mein Bereich',
      folders: projectFolders,
      files: [],
    }

    // 6. Collect all file contents from REAL storage data for seeding the fileStore.
    //    Keyed by uid (projectId + path), not path alone, so two projects that
    //    share a file path keep independent content.
    const contents: Record<string, string> = {}
    for (const project of realProjects) {
      if (Array.isArray(project.data)) {
        for (const file of project.data) {
          contents[fileUid(project.id, file.path)] = file.content ?? ''
        }
      }
    }

    // 7. Fetch all exams for the current user
    let examsRoot: TreeFolder | null = null
    try {
      const exams = await ExamService.getAllExams()
      const realExams = await Promise.all(
        exams.map((e) => ProjectService.getProject(e.id))
      )
      
      const examFolders = realExams.map((p) =>
        ProjectService.buildProjectTree(
          p.id,
          p.title ?? p.name ?? p.id,
          Array.isArray(p.data) ? p.data : []
        )
      )

      if (examFolders.length > 0) {
        examsRoot = {
          id: '__exams__',
          name: 'Meine Prüfungen',
          folders: examFolders,
          files: [],
        }
      }

      // Add exam files to contents array (uid-keyed, see step 6)
      for (const project of realExams) {
        if (Array.isArray(project.data)) {
          for (const file of project.data) {
            contents[fileUid(project.id, file.path)] = file.content ?? ''
          }
        }
      }
    } catch (e) {
      console.error('Failed to load exams for sidebar tree', e)
    }

    return { root, examsRoot, contents }
  }
}
